/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { AfterViewInit, Directive, ElementRef, HostBinding, Input, OnChanges, OnDestroy, Renderer2, RendererStyleFlags2, SimpleChanges } from '@angular/core';

export interface OrbitStickyTableConfig {
	stickyHeader?: boolean;
	stickyFirstColumn?: boolean;
	stickyRowActions?: boolean;
	existingStickyColumnIds?: readonly string[];
	rowActionColumnIds?: readonly string[];
	manageHorizontalOverflow?: boolean;
}

/**
 * Migration bridge for direct/legacy Angular Material tables.
 *
 * The directive applies the shared sticky contract without changing column
 * widths or cell content treatment. Dashboard status is deliberately a required
 * input so it cannot be inferred from a route, folder name or page size.
 */
@Directive({ selector: 'table[mat-table][orbitStickyTable], mat-table[orbitStickyTable]', standalone: false })
export class OrbitStickyTableDirective implements AfterViewInit, OnChanges, OnDestroy {
	@Input() orbitStickyTable: OrbitStickyTableConfig | '' = '';
	@Input({ required: true }) orbitTableDashboard!: boolean;

	@HostBinding('class.orbit-sticky-table') readonly stickyTableClass = true;
	@HostBinding('attr.data-orbit-dashboard') get dashboardAttribute(): string {
		return String(this.orbitTableDashboard);
	}

	private observer?: MutationObserver;

	constructor(
		private readonly elementRef: ElementRef<HTMLElement>,
		private readonly renderer: Renderer2,
	) {}

	ngAfterViewInit(): void {
		if (typeof this.orbitTableDashboard !== 'boolean') {
			throw new Error('orbitTableDashboard must explicitly identify the table as Dashboard or non-Dashboard.');
		}
		this.applyStickyContract();
		this.observer = new MutationObserver(() => this.applyStickyContract());
		this.observer.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
	}

	ngOnChanges(_: SimpleChanges): void {
		if (this.elementRef.nativeElement.isConnected) queueMicrotask(() => this.applyStickyContract());
	}

	ngOnDestroy(): void {
		this.observer?.disconnect();
	}

	private applyStickyContract(): void {
		const table = this.elementRef.nativeElement;
		const config = this.orbitStickyTable === '' ? {} : this.orbitStickyTable;
		const cells = Array.from(table.querySelectorAll<HTMLElement>('th, td, mat-header-cell, mat-cell'));
		for (const cell of cells) {
			for (const className of ['orbit-sticky-table__header-cell', 'orbit-sticky-table__first-cell', 'orbit-sticky-table__existing-cell', 'orbit-sticky-table__actions-cell', 'orbit-sticky-table__corner-left', 'orbit-sticky-table__corner-right']) {
				this.renderer.removeClass(cell, className);
			}
		}

		const headerCells = Array.from(table.querySelectorAll<HTMLElement>('th[mat-header-cell], th.mat-mdc-header-cell, mat-header-cell'));
		if (config.stickyHeader !== false) headerCells.forEach(cell => {
			this.preserveOpaqueBackground(cell);
			this.renderer.addClass(cell, 'orbit-sticky-table__header-cell');
		});

		const firstHeader = headerCells[0];
		const firstColumnClass = firstHeader ? Array.from(firstHeader.classList).find(name => name.startsWith('mat-column-')) : undefined;
		const firstCells = firstColumnClass ? cells.filter(cell => cell.classList.contains(firstColumnClass)) : [];
		if (config.stickyFirstColumn !== false) firstCells.forEach(cell => {
			this.preserveOpaqueBackground(cell);
			this.renderer.addClass(cell, 'orbit-sticky-table__first-cell');
		});

		const existingStickyColumnIds = config.existingStickyColumnIds ?? [];
		const existingStickyCells = cells.filter(cell => existingStickyColumnIds.some(id => cell.classList.contains(`mat-column-${id}`)));
		existingStickyCells.forEach(cell => {
			this.preserveOpaqueBackground(cell);
			this.renderer.addClass(cell, 'orbit-sticky-table__existing-cell');
		});

		const actionIds = config.rowActionColumnIds ?? ['actions', 'btns', '__actions'];
		const actionCells = cells.filter(cell => actionIds.some(id => cell.classList.contains(`mat-column-${id}`)));
		if (config.stickyRowActions !== false) actionCells.forEach(cell => {
			this.preserveOpaqueBackground(cell);
			this.renderer.addClass(cell, 'orbit-sticky-table__actions-cell');
		});

		for (const cell of headerCells) {
			if (cell.classList.contains('orbit-sticky-table__first-cell')) this.renderer.addClass(cell, 'orbit-sticky-table__corner-left');
			if (cell.classList.contains('orbit-sticky-table__actions-cell')) this.renderer.addClass(cell, 'orbit-sticky-table__corner-right');
		}

		const viewport = table.parentElement;
		if (viewport && config.manageHorizontalOverflow !== false) this.renderer.addClass(viewport, 'orbit-sticky-table-viewport');
	}

	private preserveOpaqueBackground(cell: HTMLElement): void {
		let element: HTMLElement | null = cell;
		let background = '';

		while (element) {
			const candidate = getComputedStyle(element).backgroundColor;
			if (candidate && candidate !== 'transparent' && candidate !== 'rgba(0, 0, 0, 0)') {
				background = candidate;
				break;
			}
			element = element.parentElement;
		}

		this.renderer.setStyle(
			cell,
			'--orbit-sticky-cell-background',
			background || 'var(--orbit-color-white, #fff)',
			RendererStyleFlags2.DashCase,
		);
	}
}
